// view/listPanel.js — createListPanel: a generic panel  [Titel + Schließkreuz] / (optional sortierbarer Header) /
// scrollbare Liste. Data-source-free: items in, onSelect/onItemHover/onClose out. Consumers (tag list im
// documents-Popup, Overlay-Panel, …) wire the actions.
//
// Extracted (frontend_view-state-base, listPanel — der 2. generische View nach listNav) aus dem wiederkehrenden Muster
// OverlayControlsPanel._buildPanel + tagListPanel: flacher topo2-Frame, Akzent-Header mit Titel + Schließkreuz, Liste scrollt.
//
// Sortierbarer Header (Chef 2026-07-06): `sorts: [{ key, label, compare(a,b), thenBy?(a,b) }]` blendet eine Sort-Leiste ein; Klick
// sortiert die Liste (aktiver Sort rosa + ▲/▼), erneuter Klick kehrt die Richtung um. Default = defaultSort || sorts[0].
// `thenBy` bricht Gleichstände und läuft IMMER aufsteigend (User 2026-10-01).
//
// Item fields (default row): label (or text), meta, metaInput (see onMetaCommit), warning: { title } — a warning
// triangle before the label, title = tooltip (e.g. a tag box that is not saved yet and why).
//
// Returns { element, setItems(items), destroy() }. Uses core/createDisposers for teardown.

import { createDisposers } from '../core/disposers.js';
import { WHEEL_SCROLL_CLASS } from '../userInput/wheelLock.js';

const CLOSE_ICON = '<span class="topo2-ic topo2-ic--close"></span>';
const WARNING_ICON = '<span class="topo2-ic topo2-ic--warning"></span>';

export function createListPanel({
    title = '',
    items = [],
    renderItem = null,          // (item, index) => HTMLElement | string  — eigener Zeilen-Inhalt; Default: label + optional meta
    onSelect = () => {},        // (item, index) => void  — Zeilen-Klick
    onItemHover = () => {},     // (item, index, entering) => void  — z.B. Tag-Box highlighten
    onClose = () => {},         // () => void  — Schließkreuz
    closeLabel = 'Schließen',
    emptyText = '—',            // Platzhalter, wenn die Liste leer ist
    sorts = [],                 // [{ key, label, compare(a,b), thenBy?(a,b) }] — optional: sortierbarer Header
    defaultSort = null,         // key des Default-Sorts (sonst sorts[0])
    // Optional: an item with `metaInput: { placeholder, title, numeric }` shows an input in the meta column instead of text
    // (`numeric: true` lets only digits in — typed or pasted)
    // (e.g. a missing PDF page to fill in). Enter → onMetaCommit(item, value, index); returning false marks the input
    // invalid and keeps it. Escape clears and leaves. While the input has focus the row is selected and onMetaFocus /
    // onMetaBlur fire — a click into the input does NOT fire onSelect.
    onMetaCommit = null,        // (item, value, index) => boolean
    onMetaFocus = () => {},     // (item, index) => void
    onMetaBlur = () => {},      // (item, index) => void
} = {}) {
    const disposers = createDisposers();
    let data = items.slice();
    let activeSort = defaultSort || (sorts[0] && sorts[0].key) || null;
    let sortDir = 1;            // 1 = aufsteigend, -1 = absteigend

    const mk = (tag, cls) => { const e = document.createElement(tag); if (cls) { e.className = cls; } return e; };
    const on = (node, ev, fn) => { node.addEventListener(ev, fn); disposers.add(() => node.removeEventListener(ev, fn)); };

    const panel = mk('div', 'topo2-panel');

    // Header (Akzent): Titel links, Schließkreuz rechts. Sticky → bleibt beim Scrollen oben.
    const header = mk('div', 'topo2-panel-header');
    const titleEl = mk('span', 'topo2-panel-title');
    titleEl.textContent = title;
    const closeBtn = mk('button', 'topo2-panel-close topo2-btn topo2-btn--icon topo2-btn--noborder topo2-btn--extrasmall topo2-btn--inset');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', closeLabel);
    closeBtn.innerHTML = CLOSE_ICON;
    on(closeBtn, 'click', () => onClose());
    header.appendChild(titleEl);
    header.appendChild(closeBtn);

    // Sort-Leiste (optional): eine Reihe klickbarer Sort-Buttons unter dem Header.
    let sortBar = null;
    if (sorts.length) {
        sortBar = mk('div', 'topo2-panel-sort');
        sorts.forEach((s) => {
            const b = mk('button', 'topo2-panel-sort-btn topo2-btn topo2-btn--noborder topo2-btn--extrasmall');
            b.type = 'button';
            b.dataset.key = s.key;
            on(b, 'click', () => {
                if (activeSort === s.key) { sortDir = -sortDir; } else { activeSort = s.key; sortDir = 1; }
                renderSortBar();
                render();
            });
            sortBar.appendChild(b);
        });
    }
    function renderSortBar() {
        if (!sortBar) { return; }
        for (const b of sortBar.children) {
            const s = sorts.find((x) => x.key === b.dataset.key);
            const active = b.dataset.key === activeSort;
            b.classList.toggle('is-active', active);
            // Auch der INAKTIVE Knopf trägt einen Pfeil — sonst liest er sich wie eine Beschriftung und nicht
            // wie ein Schalter (User 2026-09-23). Er zeigt aufwärts, weil ein Klick aufsteigend sortieren würde.
            b.textContent = s.label + (active ? (sortDir === 1 ? ' ▲' : ' ▼') : ' ▲');
        }
    }

    const list = mk('div', 'topo2-panel-list ' + WHEEL_SCROLL_CLASS);   // scrolls itself, also inside wheel-locked containers

    panel.appendChild(header);
    if (sortBar) { panel.appendChild(sortBar); }
    panel.appendChild(list);

    // Zeilen sind transient (render baut neu) → ihre Listener leben mit dem Zeilen-Element (GC beim Re-Render),
    // NICHT im DisposerBag (der ist für panel-lebenslange Listener wie den Schließkreuz).
    function buildRow(item, index) {
        const row = mk('div', 'topo2-panel-item');
        if (renderItem) {
            const content = renderItem(item, index);
            if (typeof content === 'string') { row.textContent = content; }
            else if (content) { row.appendChild(content); }
        } else {
            if (item.warning) { row.appendChild(buildWarning(item.warning)); }
            const label = mk('span', 'topo2-panel-item-label');
            label.textContent = item.label ?? item.text ?? String(item);
            row.appendChild(label);
            if (item.metaInput && onMetaCommit) {
                row.appendChild(buildMetaInput(item, index, row));
            } else if (item.meta != null && item.meta !== '') {
                const meta = mk('span', 'topo2-panel-item-meta');
                meta.textContent = item.meta;
                row.appendChild(meta);
            }
        }
        row.addEventListener('click', () => onSelect(item, index));
        row.addEventListener('mouseenter', () => onItemHover(item, index, true));
        row.addEventListener('mouseleave', () => onItemHover(item, index, false));
        return row;
    }

    function buildWarning({ title = '' }) {
        const warning = mk('span', 'topo2-panel-item-warning');
        warning.innerHTML = WARNING_ICON;
        if (title) { warning.title = title; warning.setAttribute('aria-label', title); }
        return warning;
    }

    // Input in the meta column (see onMetaCommit). Its listeners live with the row like the row's own (re-render = GC).
    function buildMetaInput(item, index, row) {
        const input = mk('input', 'topo2-panel-item-meta-input topo2-field-textinput');
        input.type = 'text';
        input.placeholder = item.metaInput.placeholder ?? '';
        if (item.metaInput.title) { input.title = item.metaInput.title; }
        const numeric = item.metaInput.numeric === true;
        if (numeric) { input.inputMode = 'numeric'; }
        input.addEventListener('click', (ev) => ev.stopPropagation());   // select the row, do not navigate
        input.addEventListener('focus', () => { row.classList.add('is-selected'); onMetaFocus(item, index); });
        input.addEventListener('blur', () => { row.classList.remove('is-selected'); onMetaBlur(item, index); });
        input.addEventListener('input', () => {
            if (numeric) { input.value = input.value.replace(/\D/g, ''); }   // also catches paste and drop
            input.classList.remove('is-invalid');
        });
        input.addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter') {
                ev.preventDefault();
                const accepted = onMetaCommit(item, input.value.trim(), index);
                input.classList.toggle('is-invalid', accepted === false);
            } else if (ev.key === 'Escape') {
                input.value = '';
                input.blur();
            }
        });
        return input;
    }

    // Aktuelle Anzeige-Reihenfolge (Kopie): der aktive Sort wird zum Render angewandt, `data` bleibt die Quelle.
    // `thenBy` nur bei Gleichstand und NICHT mit sortDir multipliziert — Seite ▼ heisst innerhalb der Seite trotzdem A→Z.
    function sortedData() {
        const s = activeSort && sorts.find((x) => x.key === activeSort);
        if (!s) { return data; }
        return data.slice().sort((a, b) => (s.compare(a, b) * sortDir) || (s.thenBy ? s.thenBy(a, b) : 0));
    }

    function render() {
        list.textContent = '';
        const rows = sortedData();
        if (!rows.length) {
            const empty = mk('div', 'topo2-panel-empty');
            empty.textContent = emptyText;
            list.appendChild(empty);
            return;
        }
        const frag = document.createDocumentFragment();
        rows.forEach((item, i) => frag.appendChild(buildRow(item, i)));
        list.appendChild(frag);
    }

    function setItems(next) {
        data = (next || []).slice();
        render();
    }

    renderSortBar();
    setItems(data);

    return {
        element: panel,
        setItems,
        destroy() { disposers.dispose(); },
    };
}
